import { apiClient } from './apiClient';
import { db } from '../lib/database';
import { syncService } from './syncService';
import { API_ENDPOINTS } from '../config/api';

export const fileUploadService = {
  uploadFiles: (submissionId, filesData, fieldTypes = {}, fieldIdMap = {}) => {
    if (submissionId.startsWith('temp_')) {
      return Promise.reject(new Error('Cannot upload files with temporary submission ID'));
    }

    const results = [];
    const errors = [];

    return Object.entries(filesData)
      .reduce((promise, [key, files]) => {
        return promise.then(() => {
          const fileArray = Array.isArray(files) ? files : [files];
          const fieldType = fieldTypes[key] || 'FILE';

          const isUUID = str => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

          // Resolve field_id to UUID if possible
          // If key is already a UUID, use it. Otherwise use the map.
          let fieldId = key;
          if (!isUUID(key)) {
            fieldId = fieldIdMap[key] || key;
          }

          const formData = new FormData();
          formData.append('submission_id', submissionId);
          formData.append('field_id', fieldId);
          formData.append('field_name', key); // Send the full key/path as field_name
          formData.append('field_type', fieldType);

          fileArray.forEach(file => {
            if (file instanceof File || file instanceof Blob) {
              let fileToUpload = file;
              if (file instanceof Blob && !(file instanceof File)) {
                fileToUpload = new File([file], file.name || `captured_image_${Date.now()}.jpg`, {
                  type: file.type || 'image/jpeg',
                  lastModified: Date.now()
                });
              }
              formData.append('files[]', fileToUpload);
            }
          });

          const endpoint = API_ENDPOINTS.submissions.uploadFiles(submissionId);
          return apiClient
            .post(endpoint, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 300000
            })
            .then(response => {
              results.push({
                fieldId,
                fileCount: fileArray.length,
                response: response.data
              });
            })
            .catch(error => {
              const errorMsg = error.response?.data?.message || error.message || 'Upload failed';
              errors.push({
                fieldId,
                fileCount: fileArray.length,
                error: errorMsg
              });
            });
        });
      }, Promise.resolve())
      .then(() => {
        if (errors.length > 0) {
          const firstError = errors[0].error;
          const error = new Error(firstError);
          error.response = { data: { message: firstError } };
          throw error;
        }
        return { success: true, results };
      });
  },

  storeFilesOffline: (submissionId, filesData, fieldTypes = {}, fieldIdMap = {}) => {
    const fileRecords = [];
    const promiseChain = Object.entries(filesData).reduce((promise, [fieldName, files]) => {
      return promise.then(() => {
        const fileArray = Array.isArray(files) ? files : [files];
        const fieldType = fieldTypes[fieldName] || 'FILE';

        return fileArray.reduce((filePromise, file, i) => {
          return filePromise.then(() => {
            if (file instanceof File || file instanceof Blob) {
              let fileToStore = file;
              if (file instanceof Blob && !(file instanceof File)) {
                fileToStore = new File([file], file.name || `captured_image_${Date.now()}.jpg`, {
                  type: file.type || 'image/jpeg',
                  lastModified: Date.now()
                });
              }
              return fileToStore.arrayBuffer().then(arrayBuffer => {
                const isUUID = str => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
                let resolvedFieldId = fieldName;
                if (!isUUID(fieldName)) {
                  resolvedFieldId = fieldIdMap[fieldName] || fieldName;
                }

                const fileRecord = {
                  id: `${submissionId}_${fieldName}_${i}_${Date.now()}`,
                  submission_id: submissionId,
                  field_id: resolvedFieldId,
                  field_name: fieldName,
                  field_type: fieldType,
                  file_name: fileToStore.name,
                  file_type: fileToStore.type,
                  file_size: fileToStore.size,
                  file_data: arrayBuffer,
                  sync_status: 'pending',
                  created_at: new Date().toISOString()
                };
                fileRecords.push(fileRecord);
              });
            }
            return Promise.resolve();
          });
        }, Promise.resolve());
      });
    }, Promise.resolve());

    return promiseChain.then(() => db.file_uploads.bulkAdd(fileRecords).then(() => fileRecords));
  },

  syncPendingUploads: (submissionId = null) => {
    let query = db.file_uploads;
    if (submissionId) {
      query = query
        .where('submission_id')
        .equals(submissionId)
        .and(file => file.sync_status === 'pending');
    } else {
      query = query.where('sync_status').equals('pending');
    }

    return query.toArray().then(pendingFiles => {
      if (pendingFiles.length === 0) {
        return { success: true, synced: 0 };
      }

      const groupedFiles = pendingFiles.reduce((acc, file) => {
        const key = `${file.submission_id}_${file.field_name}`;
        if (!acc[key]) {
          acc[key] = {
            submissionId: file.submission_id,
            fieldName: file.field_name,
            fieldType: file.field_type || 'FILE',
            files: []
          };
        }
        acc[key].files.push(file);
        return acc;
      }, {});

      let syncedCount = 0;
      const errors = [];

      return Object.values(groupedFiles)
        .reduce((promise, group) => {
          return promise.then(() => {
            const actualSubmissionId = group.submissionId;
            const fileObjects = group.files.map(fileRecord => {
              const blob = new Blob([fileRecord.file_data], { type: fileRecord.file_type });
              return new File([blob], fileRecord.file_name, { type: fileRecord.file_type });
            });

            const filesData = { [group.fieldName]: fileObjects };
            const fieldTypes = { [group.fieldName]: group.fieldType };
            const fieldIdMap = { [group.fieldName]: group.files[0]?.field_id || group.fieldName };

            return fileUploadService
              .uploadFiles(actualSubmissionId, filesData, fieldTypes, fieldIdMap)
              .then(() => {
                const fileIds = group.files.map(f => f.id);
                return db.file_uploads.bulkUpdate(
                  fileIds.map(id => ({
                    key: id,
                    changes: { sync_status: 'synced', synced_at: new Date().toISOString() }
                  }))
                );
              })
              .then(() => {
                syncedCount += group.files.length;
              })
              .catch(error => {
                errors.push({
                  submissionId: group.submissionId,
                  fieldName: group.fieldName,
                  error: error.message
                });
              });
          });
        }, Promise.resolve())
        .then(() => ({ success: errors.length === 0, synced: syncedCount, errors }));
    });
  },

  getStoredFiles: submissionId => {
    return db.file_uploads
      .where('submission_id')
      .equals(submissionId)
      .toArray()
      .then(files => {
        return files.reduce((acc, file) => {
          if (!acc[file.field_name]) {
            acc[file.field_name] = [];
          }
          acc[file.field_name].push({
            id: file.id,
            name: file.file_name,
            type: file.file_type,
            size: file.file_size,
            data: file.file_data,
            syncStatus: file.sync_status,
            createdAt: file.created_at
          });
          return acc;
        }, {});
      });
  },

  deleteSubmissionFiles: submissionId => {
    return db.file_uploads.where('submission_id').equals(submissionId).delete();
  },

  addToOutbox: (submissionId, fieldTypes = {}, fieldIdMap = {}) => {
    if (submissionId.startsWith('temp_')) return Promise.resolve();

    return db.outbox
      .where('type')
      .equals('file_upload')
      .and(item => item.payload?.submissionId === submissionId)
      .first()
      .then(existingEntry => {
        if (!existingEntry) {
          return syncService.addToOutbox({
            endpoint: API_ENDPOINTS.submissions.uploadFiles(submissionId),
            method: 'POST',
            payload: { submissionId, fieldTypes, fieldIdMap },
            description: `Upload files for submission #${submissionId}`,
            type: 'file_upload'
          });
        }
      });
  }
};
